import { useContext, useEffect, useState } from 'react';

import { CenteredLoadingBall } from '#src/components/icons/CenteredLoadingBall.tsx';
import InfoContext from '#src/contexts/info.tsx';
import WalletContext from '#src/contexts/wallet/index.tsx';
import { KeyFetchError } from '#src/lib/errors/index.ts';

import Form from './form.tsx';

interface Props {
  depositId?: string;
  onSuccess: () => void;
}

const DepositBaseBank = ({ onSuccess, depositId }: Props) => {
  const { dict } = useContext(InfoContext)!;
  const { load, wallet } = useContext(WalletContext)!;
  const [info, setInfo] = useState<{
    balance: bigint;
    gasPrice: bigint;
  } | null>(null);

  const [gas, setGas] = useState<bigint | undefined>(undefined);

  const [keyUnavailable, setKeyUnavailable] = useState(false);

  useEffect(() => {
    const loadWallet = async () => {
      try {
        await load();
      } catch (e) {
        // Without this the component sits on its loading ball forever.
        if (!(e instanceof KeyFetchError)) throw e;
        setKeyUnavailable(true);
      }
    };

    if (!wallet) loadWallet();
  }, [load, wallet]);

  useEffect(() => {
    const getInfo = async () => {
      if (wallet && !info) {
        const { ethereumWallet } = wallet;

        const [balance, gasPrice] = await Promise.all([
          ethereumWallet.getBalance(),
          ethereumWallet.getGasPrice(),
        ]);

        setInfo({ balance, gasPrice });
      }
    };

    getInfo();
  });

  useEffect(() => {
    const getGas = async () => {
      if (!gas && wallet?.starkwareWallet && info && info.balance !== 0n) {
        const { ethereumWallet } = wallet;
        const { address } = ethereumWallet;

        try {
          const computedGas = await ethereumWallet.estimateGas({
            ...ethereumWallet.depositTransaction(address),
            value: 100000000000000n.toString(),
          });
          setGas(computedGas);
        } catch (e) {
          if (!(e as Error).message?.includes('insufficient funds')) {
            throw e;
          }
        }
      }
    };

    getGas();
  });

  if (keyUnavailable)
    return <div className="helper">{dict.unableToRetrieveKeys}</div>;

  if (!wallet?.ethereumWallet || !info) return <CenteredLoadingBall />;

  if (info.balance === 0n || !gas)
    return <div className="helper">{dict.noFundsAvailable}</div>;

  return (
    <Form
      {...info}
      gas={gas}
      wallet={wallet}
      depositId={depositId}
      onSuccess={onSuccess}
    />
  );
};

export default DepositBaseBank;
